import { prefersReducedMotion } from '@theme/utilities';

// A viewport of slack, so the library starts fetching just before the animation scrolls in.
const ROOT_MARGIN = '100% 0px';

/** @type {Promise<any> | null} */
let libraryPromise = null;

/**
 * Loads the vendored lottie_light build once, shared across every instance.
 *
 * It is a UMD bundle, so it assigns globalThis.lottie rather than exporting - the ESM build is the
 * same player at 70KB gzipped versus 45KB, which is not worth paying for a global assignment.
 *
 * @param {string} src
 * @returns {Promise<any>}
 */
function loadLibrary(src) {
  libraryPromise ??= new Promise((resolve, reject) => {
    if (/** @type {any} */ (window).lottie) return resolve(/** @type {any} */ (window).lottie);

    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(/** @type {any} */ (window).lottie);
    script.onerror = () => reject(new Error('wom-lottie: failed to load the player'));
    document.head.appendChild(script);
  });

  return libraryPromise;
}

/**
 * Plays a looping Lottie animation, but only while it is on screen.
 *
 * Neither the player nor the animation JSON is fetched until the element is near the viewport, and
 * neither is fetched at all under reduced motion.
 *
 * With data-autoplay="false" the animation still loads on approach but mounts paused, leaving
 * playback to a caller - see wom-button.js, which starts it on hover and pauses it on leave.
 */
class WomLottie extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {any} */
  #animation = null;

  #loading = false;

  // Whether playback is currently wanted, so a call that lands before the animation mounts still takes effect.
  #wantsPlaying = false;

  // The frame a pending goToAndPlay asked for, replayed once the animation mounts.
  #pendingFrame = 0;

  #onScreen = false;

  get #autoplays() {
    return this.dataset.autoplay !== 'false';
  }

  connectedCallback() {
    if (prefersReducedMotion()) return;

    const { src, lib } = this.dataset;
    if (!src || !lib) return;

    this.#wantsPlaying = this.#autoplays;

    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#onScreen = entries.some((entry) => entry.isIntersecting);
        this.#onScreen ? this.#load(src, lib) : this.#animation?.pause();
      },
      { rootMargin: ROOT_MARGIN }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#animation?.destroy();
    this.#animation = null;
  }

  /** Restarts from the given frame, or queues that for whenever the animation finishes loading. */
  goToAndPlay(frame = 0) {
    this.#wantsPlaying = true;
    this.#pendingFrame = frame;
    this.#animation?.goToAndPlay(frame, true);
  }

  play() {
    this.#wantsPlaying = true;
    this.#animation?.play();
  }

  pause() {
    this.#wantsPlaying = false;
    this.#animation?.pause();
  }

  /**
   * @param {string} src
   * @param {string} lib
   */
  async #load(src, lib) {
    // Back on screen with the animation already mounted - only resume if playback is still wanted.
    if (this.#animation) {
      if (this.#wantsPlaying) this.#animation.play();
      return;
    }
    if (this.#loading) return;

    this.#loading = true;

    try {
      const lottie = await loadLibrary(lib);

      // Disconnected while the player was in flight - drop it rather than mounting into a detached node.
      if (!this.isConnected) return;

      this.#animation = lottie.loadAnimation({
        container: this,
        renderer: 'svg',
        loop: true,
        autoplay: this.#autoplays,
        path: src,
      });

      // Hover may have come and gone while the JSON was in flight - settle to whatever state won.
      if (this.#wantsPlaying && this.#onScreen) {
        this.#animation.goToAndPlay(this.#pendingFrame, true);
      } else {
        this.#animation.pause();
      }
    } catch {
      // A missing player just means no fire - the artwork behind it still renders.
      this.#observer?.disconnect();
      this.#observer = null;
    } finally {
      this.#loading = false;
    }
  }
}

if (!customElements.get('wom-lottie')) {
  customElements.define('wom-lottie', WomLottie);
}
